/**
 * Release-notes feed for the in-app "What's New" page (/whats-new).
 *
 * Convention: each release is labelled by month (e.g. "July 2026") — a
 * date-based label reads cleanly on a client-facing changelog and is
 * honest about cadence. Newest release first.
 *
 * Adding a release: prepend a new object to RELEASES.
 * Screenshots: drop images into public/images/releases/<YYYY-MM>/ and
 * reference them with absolute paths like
 * '/images/releases/2026-07/monthly-volume.png'. Missing images are
 * hidden gracefully, so you can ship the copy first and add shots later.
 *
 * Body copy supports a tiny markdown subset:
 *   **bold**   → emphasised term
 *   `code`     → literal value
 *   "- " line  → bullet
 *   blank line → paragraph break
 */

export interface ReleaseScreenshot {
  src: string;
  label?: string;
}

export interface ReleaseSection {
  title: string;
  body: string;
  screenshot?: string;
  screenshots?: ReleaseScreenshot[];
}

export interface ReleaseNote {
  title: string;
  body: string;
}

export interface Release {
  /** Month label shown as the release heading, e.g. "July 2026". */
  version: string;
  /** ISO date (YYYY-MM-DD) — drives ordering + the formatted date. */
  date: string;
  /** Product area this release belongs to (e.g. "Depletions",
   *  "Distribution", "Marketing") — shown as a colored tag. */
  area?: string;
  title: string;
  summary: string;
  sections: ReleaseSection[];
  /** Optional client-facing "for your awareness" callouts. */
  dataNotes?: ReleaseNote[];
}

const RELEASES: Release[] = [
  {
    version: 'November 2026',
    date: '2026-11-04',
    area: 'Ask ABHI',
    title: 'Meet Ask ABHI, Your Data Assistant',
    summary:
      'Ask questions about depletions, accounts, products and visit notes in plain English, and get answers straight from our data — with a source for every number.',
    sections: [
      {
        title: 'Ask in plain English',
        body: 'Open **Ask ABHI** from the button in the bottom-right corner, or press `⌘J` / `Ctrl+J`. Try questions like:\n- "Top 10 accounts in Florida this year"\n- "How is Bottled in Bond trending in Texas?"\n- "Which accounts haven\'t ordered in the last 3 months?"\n- "Summarize my visits from the last 30 days"\n\nAsk a follow-up in the same chat ("what about Georgia?"), or pick one of the suggested questions under each answer.',
      },
      {
        title: 'Answers you can check',
        body: "Every figure comes from the platform's own data, and each one is checked against it before you see it. If a number can't be verified, Ask ABHI says so instead of guessing. Click any source under an answer to see the exact rows — or the visit note — behind it.\n\nVolumes are shown in **6-packs**, the same as the rest of the dashboard.",
      },
      {
        title: 'What it can see',
        body: "Ask ABHI only answers from what your role can already see: depletions data, visit notes, or both. Reps see their own notes and their accounts' notes, but not other reps' activity. It doesn't browse the web or answer general questions.",
      },
      {
        title: 'Tell us how it did',
        body: 'Use 👍 / 👎 under any answer — a thumbs-down lets you say what went wrong. Questions and answers are reviewed to help improve future answers. Admins can see usage, feedback and answers to review under **Admin → Ask ABHI**.',
      },
    ],
    dataNotes: [
      {
        title: 'Beta',
        body: 'Ask ABHI is new. Each person can ask up to 100 questions a day, and the latest month is usually partial — answers say when they include it.',
      },
    ],
  },
  {
    version: 'July 2026',
    date: '2026-07-30',
    area: 'Depletions',
    title: 'Online Retailers, a 2026 Orders Log & Deeper Premises Views',
    summary:
      'A new online-retailer channel flagged across the app, a 2026 orders log you can slice by month, and Premises tiles you can filter by state and open to the exact accounts behind them.',
    sections: [
      {
        title: 'Online Retailers — a channel of their own',
        body: "Your e-commerce / online accounts (e.g. **Mr. Daisy**, **Jensen's Liquors**, **Oasis**) are now treated as a distinct **Online Retailers** channel instead of being lumped into Off-Premise:\n- They show up as a **4th tile** in the **Premises Mix**, with their own volume, share, and year-over-year trend — carved out of Off-Premise.\n- A violet **Online** badge now flags these accounts **everywhere they appear** — Follow-Up Tracker, the field team's Today's Plan, Top Accounts, and the account drill-downs.\n- The premises filters include an **Online** option so you can isolate the channel in one click.",
        screenshot: '/images/releases/2026-07/online-retailers.png',
      },
      {
        title: 'New section: 2026 Orders',
        body: "A new **2026 Orders** section lists every account's depletions **by month** for the year:\n- Filter to a **specific month**, the **last 3 months (~90 days)**, or the **full year**; **sort** by volume, account, or activity; and **search** by account or city.\n- **Open any account** to see its order **each month, broken down by product** — with a color key and hover detail.\n- Depletions are reported by month, so a month with **no order** is shown clearly rather than skipped.",
        screenshot: '/images/releases/2026-07/2026-orders.png',
      },
      {
        title: 'Filter & drill into accounts — by state and by channel',
        body: 'Slicing the account base is now much easier:\n- **Premises Mix — filter by state**, with the state picker marking **ABC "control" states** so control markets stand out at a glance.\n- **Open any Premises tile to see its accounts** — click Off-Premise (or any channel) to list the accounts in it, each showing what it **carries vs. is missing**, with search and sort.\n- **Top Accounts — filter by channel** with a chip row (**On · Off · Online · NA · Unknown**) to narrow to a single channel in one click.',
        screenshots: [
          {
            src: '/images/releases/2026-07/premises-state-filter.png',
            label: 'State filter + control states',
          },
          {
            src: '/images/releases/2026-07/premises-account-drilldown.png',
            label: 'Accounts behind a tile',
          },
          {
            src: '/images/releases/2026-07/top-accounts-premises-filter.png',
            label: 'Top Accounts by channel',
          },
        ],
      },
    ],
    dataNotes: [
      {
        title: 'Florida coverage expanded',
        body: 'Depletions from the Florida **Greenlight** feed are now loaded, adding Florida retail and on-premise accounts across the dashboard.',
      },
    ],
  },
  {
    version: 'July 2026',
    date: '2026-07-21',
    area: 'Depletions',
    title: 'Filtering, Volume Metrics & Deeper Drill-Downs',
    summary:
      'Filter the volume trend to exactly the window you care about, rank products and states by the cases they actually move, and open any one for its full year-over-year history.',
    sections: [
      {
        title: 'Filter the Monthly Depletion Volume chart',
        body: 'The **Monthly Depletion Volume** chart now has a filter bar so you can slice the view yourself:\n- Toggle which **years** are overlaid — compare any subset (e.g. just 2025 vs 2026).\n- Set a **month window** (start → end) or use a quick preset: **Full Year**, **YTD**, or **Q1–Q4**.\n- The summary tiles (Total, Avg, Peak, Lowest, Avg Accounts) recompute to match what you have selected.\n\nA small **"How to filter"** popover on the bar explains each control.',
        screenshot: '/images/releases/2026-07/monthly-volume-filter.png',
      },
      {
        title: 'Product & State performance now measured by volume',
        body: "The performance KPIs now rank by **6-pack volume**, not percentage — a big product adding real cases beats a tiny one that doubled off a small base:\n- **Top Mover** = the product/state that added the most volume quarter-over-quarter.\n- **Biggest Slipper** = the one that lost the most volume.\n- **Concentration** now names the **top-3 products/states** on hover, with each one's share.\n\nA short note above the KPIs makes it explicit that every figure is volume-based.",
        screenshots: [
          {
            src: '/images/releases/2026-07/product-performance-kpis-by-volume.png',
            label: 'Product Performance',
          },
          {
            src: '/images/releases/2026-07/state-performance-kpis-by-volume.png',
            label: 'State Performance',
          },
        ],
      },
      {
        title: 'On- vs Off-Premise mix, cleaned up',
        body: 'The **Premises Mix** section is clearer:\n- **NA** and **Unknown** are combined into a single **"Other"** category, with a small breakdown so you can still see the NA vs Unknown split.\n- The year-end **pace projection** now reads more naturally — the projected full-year figure (**↗ pace**) and the **actual** shipped so far are shown side by side next to each bar.',
        screenshot: '/images/releases/2026-07/on-off-premise-mix-cleanup.png',
      },
      {
        title: 'Drill into each product’s year-over-year timeline',
        body: 'Expand any row in **Product Performance** to see that product’s **full monthly history as a year-over-year chart** — one line per year over a shared Jan–Dec axis, with the same year/month filters as the main trend. The fastest way to check whether this March beat last March for a given SKU.',
        screenshot: '/images/releases/2026-07/product-yoy-timeline.png',
      },
      {
        title: 'See a state’s products over time',
        body: 'Expand any row in the **State Breakdown** to see a **year-over-year mini chart for every product** that state sells — compare the same month across years, product by product, with product and year filters so you can focus on what matters.',
        screenshot: '/images/releases/2026-07/state-products-yoy.png',
      },
    ],
  },
  {
    version: 'July 2026',
    date: '2026-07-14',
    area: 'Depletions',
    title: 'Redesigned Volume Trend & Smarter Follow-Ups',
    summary:
      'The monthly volume trend, redesigned for year-over-year comparison — plus follow-up ranking that surfaces the accounts, and the missing products, worth a call first.',
    sections: [
      {
        title: 'Monthly Volume Trend redesigned as year-over-year',
        body: 'The **Depletions – Monthly Volume Trend** now plots **one line per year** over a shared Jan–Dec axis, so you can compare the **same month across years** at a glance. Hover any month for the year-over-year breakdown.',
        screenshot: '/images/releases/2026-07/monthly-volume-yoy.png',
      },
      {
        title: 'New Missing Products column',
        body: 'The **Follow-Up Tracker** now shows a **Missing Products** column — the catalog SKUs each account hasn’t bought yet, ordered by how well they sell elsewhere — so a rep can see exactly what to pitch on the next visit.',
        screenshot: '/images/releases/2026-07/missing-products.png',
      },
      {
        title: 'Ranking now flags accounts missing Rye or Bourbon',
        body: 'The ranking criteria were enhanced to consider accounts **missing key products**. Any account that doesn’t carry **both a Rye and a Bourbon** is bumped to the top of the list so the **core whiskey line** gets pitched first — on both the dashboard Follow-Up Tracker and the field team’s Today’s Plan.',
        screenshot: '/images/releases/2026-07/ranking-core-gap.png',
      },
    ],
  },
];

export default RELEASES;
